import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

import type { CohortBuilderDefinitionLegacy } from '@verantos-dev/types-cohorts';

import { createAVSlice } from './slices/avSlice';
import { createConceptGroupSlice } from './slices/conceptGroupSlice';
import { createLegacyEntitiesSlice } from './slices/legacyEntitiesSlice';
import { createModalSlice } from './slices/modalSlice';
import { createProfileSlice, initialProfile, initialStudyPeriod } from './slices/profileSlice';
import type { CohortDefinitionStoreActions, CohortDefinitionStoreState } from './types';

// ---------------------------------------------------------------------------
// Re-exports — all public APIs stay importable from this file
// ---------------------------------------------------------------------------

export { applyStatusGate } from './statusGate';

export {
  selectActiveCohortBuilderItem,
  selectAnalyticalVariables,
  selectAVById,
  selectAvCriterionSourceLibraryAvId,
  selectCohortBuilderItemViewed,
  selectConceptGroups,
  selectConceptGroupSelections,
  selectConceptGroupSelectionsWithIssues,
  selectConceptGroupsConfidence,
  selectCriteria,
  selectCriteriaSelectionByAVId,
  selectDerivedEvents,
  selectHasConceptGroupErrors,
  selectHasStagedChanges,
  selectHasUnresolvedStubs,
  selectIndexDate,
  selectIsEditable,
  selectIsOwner,
  selectUsedInAnalyticalDatasets,
  selectIsExecutionPending,
  selectIsIndexDateAV,
  selectIsSaving,
  selectLatestCohortVersionId,
  selectLatestDatasetReleaseId,
  selectLatestDatasetReleaseDisplayName,
  selectLatestStatus,
  selectLatestSubjectCount,
  selectModal,
  selectPendingPreviewAvIds,
  selectRecentlyAppliedForAV,
  selectSimpleEvents,
  selectStudyPeriodChangedBy,
  selectStudyPeriodChangeStatus,
  selectStudyPeriodEndDate,
  selectStudyPeriodStartDate,
  selectSubjectVariables,
  selectValidationNonce,
} from './selectors';

export type {
  AnalyticalDatasetUsageRef,
  AnalyticalVariableWithChangeStatus,
  CohortProfileState,
  LegacyEntities,
  QueuedEventModalFilterContext,
  QueuedEventModalSelection,
  StudyPeriodState,
} from './types';

// ---------------------------------------------------------------------------
// Initial state (composed from slice defaults)
// ---------------------------------------------------------------------------

const initialState: CohortDefinitionStoreState = {
  analyticalVariables: [],
  criteria: [],
  hasStagedChanges: false,
  // TODO: cleanup with asset_edit_while_in_use ff
  assetEditWhileInUseEnabled: false,
  // TODO: cleanup with skip_process_confirmation_modal ff
  skipProcessConfirmationModalEnabled: false,
  agentRefetchPending: false,
  loadedCohortId: undefined,
  validationRequests: {},
  conceptGroupErrorCounts: {},
  studyPeriod: initialStudyPeriod,
  profile: initialProfile,
  simpleEvents: [],
  derivedEvents: [],
  indexDate: undefined,
  subjectVariables: [],
  recentlyAppliedConceptGroups: [],
  pendingPreviewAvIds: [],
  conceptGroups: undefined,
  conceptGroupSelections: [],
  conceptGroupsConfidence: undefined,
  modal: undefined,
  queuedEventModals: undefined,
  queuedEventModalsConceptGroupId: undefined,
  activeCohortBuilderItem: undefined,
  viewedCohortBuilderItems: {},
};

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

export const useCohortDefinitionStore = create<
  CohortDefinitionStoreState & CohortDefinitionStoreActions
>()(
  devtools(
    (set, get) => ({
      ...createAVSlice(set, get),
      ...createProfileSlice(set),
      ...createLegacyEntitiesSlice(set),
      ...createConceptGroupSlice(set),
      ...createModalSlice(set, get),
      setLoadedCohortId: (loadedCohortId: string | undefined) => set({ loadedCohortId }),
      reset: () => set(initialState),
    }),
    { name: 'cohort-definition-store' }
  )
);

// ---------------------------------------------------------------------------
// Definition builder — single source of truth for building CohortBuilderDefinitionLegacy
// ---------------------------------------------------------------------------

/**
 * Resolves once `profile.isSaving` is false, or after `timeoutMs` as a fallback.
 *
 * Use this from non-React code (e.g. socket emit handlers) that should hold off
 * until an in-flight DynamoDB write is acknowledged by `staged_changes_updated`.
 * The 5s timeout matches the upper bound of typical DynamoDB write latency and
 * ensures send paths can't hang indefinitely on a lost socket ack.
 *
 * TODO: cleanup with explicit_save ff
 */
export const awaitNotSaving = (timeoutMs = 5_000): Promise<void> => {
  if (!useCohortDefinitionStore.getState().profile.isSaving) {
    return Promise.resolve();
  }
  return new Promise<void>((resolve) => {
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      unsub();
      clearTimeout(timer);
      resolve();
    };
    const unsub = useCohortDefinitionStore.subscribe((s) => {
      if (!s.profile.isSaving) finish();
    });
    const timer = setTimeout(finish, timeoutMs);
  });
};

export const buildDefinitionFromStore = (
  overrides?: Partial<CohortBuilderDefinitionLegacy>
): CohortBuilderDefinitionLegacy => {
  const state = useCohortDefinitionStore.getState();
  return {
    simpleEvents: state.simpleEvents,
    derivedEvents: state.derivedEvents,
    indexDate: state.indexDate,
    subjectVariables: state.subjectVariables,
    analyticalVariables: state.analyticalVariables,
    criteria: state.criteria,
    studyPeriodRestrictions: {
      overallStudyPeriodRestriction: state.studyPeriod.overallStudyPeriodRestriction,
      baselinePeriodRestriction: undefined,
      followUpPeriodRestriction: undefined,
      changeStatus: state.studyPeriod.changeStatus,
      changedBy: state.studyPeriod.changedBy,
    },
    ...overrides,
  };
};
